import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { supabase } from '@/lib/supabase';
import { usePageMeta } from '@/hooks/usePageMeta';

interface Guide {
  id: string;
  title: string;
  summary: string;
  tag: string;
  read_time: string;
  author: string;
  image: string;
  created_at: string;
}

export default function Guides() {
  const [guides, setGuides] = useState<Guide[]>([]);
  const [loading, setLoading] = useState(true);

  usePageMeta('Buying Guides', 'In-depth buying guides written by category specialists to help you make the right choice, every time.');

  useEffect(() => {
    supabase.from('buying_guides').select('*').order('created_at', { ascending: false }).then(({ data }) => {
      if (data) setGuides(data);
      setLoading(false);
    });
  }, []);

  return (
    <div className="min-h-screen bg-white text-ink-900 antialiased">
      <Header />
      <main className="pt-28 md:pt-36 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-ink-900 tracking-tight">Buying Guides</h1>
          <p className="mt-4 text-lg text-ink-600 max-w-2xl leading-relaxed">
            In-depth guides written by category specialists to help you make the right choice, every time.
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            {guides.map(g => (
              <Link to={`/guides/${g.id}`} key={g.id} className="group relative overflow-hidden rounded-2xl bg-white border border-ink-100 shadow-soft hover:-translate-y-1 transition duration-300 block">
                <div className="relative aspect-[16/10] overflow-hidden bg-ink-50">
                  <img
                    src={g.image}
                    alt={g.title}
                    className="w-full h-full object-contain p-4 group-hover:scale-105 transition duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-900/50 via-transparent to-transparent"></div>
                  <span className="absolute top-4 left-4 h-7 px-3 rounded-full bg-white/90 backdrop-blur text-ink-900 text-[11px] font-bold uppercase tracking-wider flex items-center">
                    {g.tag}
                  </span>
                </div>
                <div className="p-6">
                  <h2 className="font-display text-xl font-bold text-ink-900 leading-tight">{g.title}</h2>
                  <p className="mt-2 text-sm text-ink-600 leading-relaxed line-clamp-2">{g.summary}</p>
                  <div className="mt-4 flex items-center gap-3 text-xs text-ink-400">
                    <span>{g.author}</span>
                    <span>&middot;</span>
                    <span>{g.read_time}</span>
                    <span>&middot;</span>
                    <span>{new Date(g.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                    <span className="ml-auto text-cta-600 font-semibold flex items-center gap-1 whitespace-nowrap">
                      Read Guide
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-line text-base"></i>
                      </span>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}